import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ISelectedItem } from "@/data/model/Bill";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { IPaymentIntent, IPaymentIntentConfirmHandle, IPaymentMethod } from "@/data/model/Customer";
import { FulfillmentTimeMode, PaymentFor, PaymentMethodMode, PaymentProvider } from "@/data/model/Enums";
import { getIsApplyVouchersAllowedForOrderUseCase } from "@/data/services/PaymentService";
import { config as dataConfig, exceptionReasons, exceptionTypes } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { JavascriptLoader } from "@/data/util/JavascriptLoader";
import { Misc } from "@/data/util/Misc";
import { Signal } from "@/data/util/Signal";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";
import {
    sendWalleeTransactionToAuthorize,
    sendWorldlineTransactionToAuthorize,
} from "@/shared/utils/androidNativeUtils";

import { ConnectionState } from "@/components/websocket/ConnectionState";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import type { IStripeFormValues } from "@/app/elements/StripeCardForm";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export enum PaymentState {
    Preparing,
    MethodSelection,
    ReadyToPay,
    Paying,
    Confirmed,
    Failed,
    Ordering, //For pay later orders
}

export class PayContext {
    private customerDao: CustomerDao;

    public onPaymentAccepted?: { (transactionId: string, paymentFor: PaymentFor) };
    public onCartEmptyGoback?: { () };
    public onGoBackToFulfillmentTimeView?: { () };

    public state: PaymentState;

    public defaultTipPercent: number;

    public consumption: number;
    public discountAmount: number;
    public totalExclTipAfterDiscounts: number;
    public tipPercent?: number;
    public tipAmount: number;
    public total: number;

    public intent?: IPaymentIntent;
    public selectedPaymentMethod?: IPaymentMethod;

    public onAmountChanged: Signal;
    public onStateChanged: Signal;
    public onIntentChanged: Signal;
    public onBillDiscountChanged: Signal;

    private selectedCreditCard?: ICreditCard;
    private paymentInProgress = false;
    private walleeHandler: any;
    private stripeHandler?: { (): IStripeFormValues };

    constructor(defaultTipPercent?: number) {
        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

        this.defaultTipPercent = defaultTipPercent == null ? config.defaultTip : defaultTipPercent / 100;
        this.consumption = 0;
        this.discountAmount = 0;
        this.totalExclTipAfterDiscounts = 0;
        this.total = 0;
        this.tipPercent = this.defaultTipPercent;
        this.tipAmount = 0;
        this.state = PaymentState.Preparing;

        this.onAmountChanged = new Signal();
        this.onStateChanged = new Signal();
        this.onIntentChanged = new Signal();
        this.onBillDiscountChanged = new Signal();

        this.renewConsumption();
    }

    public getTotalAfterDiscountsAndVouchers() {
        return this.totalExclTipAfterDiscounts + this.tipAmount;
    }

    public setDefaultTipInPercent(defaultTip: number | undefined) {
        this.defaultTipPercent = defaultTip == null ? config.defaultTip : defaultTip / 100;
        this.tipPercent = this.defaultTipPercent;
        this.updateTotal();
    }

    public setTipInPercent(tip: number) {
        this.tipPercent = tip;
        this.updateTotal();
    }

    public setTip(tip: number) {
        this.tipPercent = undefined;
        this.tipAmount = tip;
        this.updateTotal();
    }

    public async prepareForCheckout(): Promise<void> {
        this.selectedPaymentMethod = undefined;
        this.selectedCreditCard = undefined;
        this.state = PaymentState.Preparing;
        this.onStateChanged.dispatch();

        const isPayService = Helper.isPayService();
        try {
            let intent;
            if (isPayService) {
                intent = await this.customerDao.prepareBillForCheckout({
                    payServiceId: TastierClient.context.selectedServiceId!,
                });
            } else {
                intent = await this.customerDao.prepareCartForCheckout({
                    cartHash: TastierClient.context.currentOffer!.cart!.hash,
                    orderServiceId: TastierClient.context.selectedServiceId!,
                });
            }

            this.intent = intent;
            this.onIntentChanged.dispatch(intent);
            this.checkState();
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (!isPayService && e.type === exceptionTypes.cartIsEmptyException) {
                if (this.onCartEmptyGoback !== undefined) {
                    this.onCartEmptyGoback();
                }
            } else {
                Popup.show(e.message);
            }
            throw ex;
        }
    }

    public isPayLater(): boolean {
        return this.selectedPaymentMethod?.id === dataConfig.payLaterPaymentMethod;
    }

    public isTipAllowedForSelectedPaymentMethod(): boolean {
        return this.selectedPaymentMethod == null || this.selectedPaymentMethod.isTipAllowed;
    }

    public async pay(
        selectedItems: ISelectedItem[],
        consumption: number,
        force?: boolean,
        onClickBackBtn?: () => void,
        anonymousUserEmail?: string,
        endUserEmail?: string
    ) {
        if (this.isPayLater()) {
            this.state = PaymentState.Ordering;
        } else {
            this.state = PaymentState.Paying;
        }

        this.paymentInProgress = true;
        this.onStateChanged.dispatch();

        const paymentMethod = this.selectedCreditCard != null ? undefined : this.selectedPaymentMethod!.id;
        const paymentMethodMode =
            this.selectedCreditCard != null ? PaymentMethodMode.Online : this.selectedPaymentMethod!.mode;
        const paymentMethodProvider =
            this.selectedCreditCard != null ? this.selectedCreditCard.provider : this.selectedPaymentMethod!.provider;

        const isPayService = Helper.isPayService();
        if (isPayService) {
            try {
                const selectedBill = TastierClient.context.currentBill;
                if (selectedBill == null) {
                    throw "No selected bill";
                }

                const e = await this.customerDao.checkoutBill({
                    creditCardId: this.selectedCreditCard === undefined ? undefined : this.selectedCreditCard.id,
                    tip: this.tipAmount,
                    consumption: consumption,
                    billId: selectedBill.id,
                    force: force === undefined ? false : force,
                    payServiceId: TastierClient.context.selectedServiceId!,
                    paymentMethod: paymentMethod,
                    paymentMethodMode: paymentMethodMode,
                    paymentProvider: paymentMethodProvider,
                    selectedItems: selectedItems,
                    socketConnectionId: TastierClient.context.socket.connectionId!,
                    deviceFingerprint: await DeviceFingerprintProvider.getFingerprint(),
                });

                if (e?.wasPaymentCoveredByDiscounts === true) {
                    this.paymentInProgress = false;
                } else {
                    if (
                        paymentMethodProvider === PaymentProvider.Wallee ||
                        paymentMethodProvider === PaymentProvider.PostFinance
                    ) {
                        await this.onCheckoutBillWithWallee(e!);
                    } else if (paymentMethodProvider === PaymentProvider.Stripe) {
                        if (e == null) {
                            this.paymentInProgress = false;
                            return;
                        }

                        //stripe needs additional confirmation handling
                        if (!e.isAlreadyConfirmed) {
                            let paymentMethod = undefined as any;
                            if (this.selectedCreditCard === undefined && this.stripeHandler !== undefined) {
                                const vals = this.stripeHandler();
                                if (vals !== undefined) {
                                    paymentMethod = {
                                        payment_method: {
                                            billing_details: {
                                                name: vals.cardHolder,
                                            },
                                            card: vals.cardnoElement,
                                        },
                                        save_payment_method: true,
                                        setup_future_usage: "off_session",
                                    };
                                }
                            }

                            try {
                                const s = await JavascriptLoader.requireStripe();
                                const a = await s.confirmCardPayment(e.intentSecret, paymentMethod);
                                if (a.error === undefined) {
                                    if (this.onPaymentAccepted !== undefined)
                                        this.onPaymentAccepted(e.intentId, PaymentFor.Bill);

                                    this.paymentInProgress = false;
                                } else {
                                    if (a.error.code === "card_declined")
                                        Popup.show(translations.pay.failedPaymentInvalidCard);
                                    else Popup.show(translations.pay.failedPaymentUnknownReason);

                                    this.paymentInProgress = false;
                                    this.checkState();

                                    if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                                }
                            } catch (ex) {
                                logError(ex);
                                Popup.show(translations.pay.failedPaymentUnknownReason);
                                this.paymentInProgress = false;
                                this.checkState();

                                if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                            }
                        } else {
                            if (this.onPaymentAccepted !== undefined)
                                this.onPaymentAccepted(e.intentId, PaymentFor.Bill);

                            this.paymentInProgress = false;
                        }
                    } else if (paymentMethodProvider === PaymentProvider.Worldline) {
                        await this.onCheckoutWithWorldline(e!);
                    } else {
                        this.paymentInProgress = false;
                    }
                }
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                this.onCheckoutBillError(selectedItems, consumption, e, onClickBackBtn);
            }
        } else {
            try {
                const command = {
                    billId: TastierClient.context.currentBill?.id,
                    creditCardId: this.selectedCreditCard === undefined ? undefined : this.selectedCreditCard.id,
                    cartHash: TastierClient.context.currentOffer!.cart!.hash,
                    tip: this.tipAmount,
                    force: force === undefined ? false : force,
                    orderServiceId: TastierClient.context.selectedServiceId!,
                    paymentMethod: paymentMethod,
                    paymentProvider: paymentMethodProvider,
                    socketConnectionId: TastierClient.context.socket.connectionId!,
                    deviceFingerprint: await DeviceFingerprintProvider.getFingerprint(),
                    anonymousUserEmail: anonymousUserEmail,
                    endUserEmail: endUserEmail,
                };
                const intentConfirmHandle = await this.customerDao.checkoutCart(command);

                this.paymentInProgress = true;

                if (intentConfirmHandle?.wasPaymentCoveredByDiscounts === true) {
                    this.paymentInProgress = false;
                } else {
                    if (
                        paymentMethodProvider === PaymentProvider.Wallee ||
                        paymentMethodProvider === PaymentProvider.PostFinance
                    ) {
                        await this.onCheckoutCartWithWallee(intentConfirmHandle!);
                    } else if (paymentMethodProvider === PaymentProvider.Stripe) {
                        await this.checkoutCartWithStripe(intentConfirmHandle!);
                    } else if (paymentMethodProvider === PaymentProvider.Worldline) {
                        await this.onCheckoutWithWorldline(intentConfirmHandle!);
                    } else {
                        this.paymentInProgress = false;
                    }

                    if (intentConfirmHandle?.isWalleePerformTransactionSucceeded === false) {
                        throw new DaoError("wallee_perform_transaction_failed", "Wallee transaction failed.");
                    }
                }
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                this.onCheckoutCartError(e, onClickBackBtn, anonymousUserEmail, endUserEmail);
            }
        }
    }

    private resetCartIfPossible() {
        if (TastierClient.context.currentOffer != null) {
            TastierClient.context.currentOffer.cart = undefined;
        }
    }

    private async checkoutCartWithStripe(e: IPaymentIntentConfirmHandle): Promise<void> {
        //stripe needs additional confirmation handling
        if (e == null) {
            this.paymentInProgress = false;
            return;
        }

        if (!e.isAlreadyConfirmed) {
            this.resetCartIfPossible();
            let paymentMethod = undefined as any;
            if (this.selectedCreditCard === undefined && this.stripeHandler !== undefined) {
                const vals = this.stripeHandler();
                if (vals !== undefined) {
                    paymentMethod = {
                        payment_method: {
                            billing_details: {
                                name: vals.cardHolder,
                            },
                            card: vals.cardnoElement,
                        },
                        save_payment_method: true,
                        setup_future_usage: "off_session",
                    };
                }
            }

            try {
                const s = await JavascriptLoader.requireStripe();
                const a = await s.confirmCardPayment(e.intentSecret, paymentMethod);

                if (a.error === undefined) {
                    if (this.onPaymentAccepted !== undefined) this.onPaymentAccepted(e.intentId, PaymentFor.Order);

                    this.paymentInProgress = false;
                } else {
                    if (a.error.code === "card_declined") Popup.show(translations.pay.failedPaymentInvalidCard);
                    else Popup.show(translations.pay.failedPaymentUnknownReason);

                    this.paymentInProgress = false;
                    this.checkState();

                    if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                }
            } catch (ex) {
                logError(ex);
                Popup.show(translations.pay.failedPaymentUnknownReason);
                this.paymentInProgress = false;
                this.checkState();

                if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
            }
        } else {
            if (this.onPaymentAccepted !== undefined) this.onPaymentAccepted(e.intentId, PaymentFor.Order);

            this.paymentInProgress = false;
        }
    }

    private onCheckoutBillError(
        selectedItems: ISelectedItem[],
        consumption: number,
        e: DaoError,
        onClickBackBtn?: () => void
    ) {
        this.paymentInProgress = false;
        logError(e);

        if (e.type === exceptionTypes.billPaymentsInProgressException) {
            Popup.show(
                translations.pay.failedBillPaymentInProgress,
                () => {
                    if (onClickBackBtn !== undefined) {
                        onClickBackBtn();
                    }
                },
                translations.general.ok,
                undefined,
                "pmtconfirm"
            );
        } else if (
            e.type === exceptionTypes.billInvalidSelectionException ||
            e.type === exceptionTypes.billIsEmptyException ||
            e.type === exceptionTypes.billNoSelectedItemsException
        ) {
            Popup.show(
                translations.pay.failedBillInvalidSelection,
                () => {
                    if (onClickBackBtn !== undefined) {
                        onClickBackBtn();
                    }
                },
                translations.general.ok,
                undefined,
                "pmtconfirm"
            );
        } else if (e.type === exceptionTypes.paymentInProgressException) {
            if (e.additionalData.reason === "payment_in_progress") {
                Popup.show(
                    translations.pay.paymentInProgressWarning,
                    () => {
                        this.pay(selectedItems, consumption, true, onClickBackBtn);
                    },
                    translations.pay.btnOrderAgain,
                    translations.general.cancel,
                    "pmtconfirm",
                    true
                );
            } else if (e.additionalData.reason === "payment_recently_done") {
                Popup.show(
                    translations.pay.paymentRecentlyDoneWarning,
                    () => {
                        this.pay(selectedItems, consumption, true, onClickBackBtn);
                    },
                    translations.pay.btnOrderAgain,
                    translations.general.cancel,
                    "pmtconfirm",
                    true
                );
            } else {
                Popup.show(translations.pay.failedPaymentInProgress);
            }

            this.checkState();
        } else if (e.type === exceptionTypes.stripeException) {
            Popup.show(translations.pay.failedPaymentUnknownReason + " " + e.message);
            this.checkState();
        } else if (e.type === exceptionTypes.walleePaymentMethodNotAvailableException) {
            Popup.show(translations.pay.walleePaymentNotAvailable);
            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.outOfStockException) {
            let itemList = "<ul>";
            for (const i in e.additionalData.remaining)
                itemList +=
                    "<li>" +
                    Format.htmlEscape(e.additionalData.remaining[i].articleName) +
                    ": " +
                    e.additionalData.remaining[i].remaining +
                    "</li>";

            itemList += "</ul>";

            const msg = translations.pay.failedOutOfStock.replace("{0}", itemList);
            Popup.show(msg);
            this.checkState();
        } else {
            Popup.show(translations.pay.failedPaymentUnknownReason);
            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        }
    }

    private onCheckoutCartError(
        error: DaoError,
        onClickBackBtn?: () => void,
        anonymousUserEmail?: string,
        endUserEmail?: string
    ) {
        this.paymentInProgress = false;
        logError(error);

        if (error.type === exceptionTypes.paymentInProgressException) {
            if (error.additionalData.reason === "payment_in_progress") {
                Popup.show(
                    translations.pay.paymentInProgressWarning,
                    () => {
                        this.pay([], this.consumption, true, onClickBackBtn, anonymousUserEmail, endUserEmail);
                    },
                    translations.pay.btnOrderAgain,
                    translations.general.cancel,
                    "pmtconfirm",
                    true
                );
            } else if (error.additionalData.reason === "payment_recently_done") {
                Popup.show(
                    translations.pay.paymentRecentlyDoneWarning,
                    () => {
                        this.pay([], this.consumption, true, onClickBackBtn, anonymousUserEmail, endUserEmail);
                    },
                    translations.pay.btnOrderAgain,
                    translations.general.cancel,
                    "pmtconfirm",
                    true
                );
            } else Popup.show(translations.pay.failedPaymentInProgress);

            this.checkState();
        } else if (error.type === exceptionTypes.stripeException) {
            Popup.show(translations.pay.failedPaymentUnknownReason + " " + error.message);
            this.checkState();
        } else if (error.type === exceptionTypes.walleePaymentMethodNotAvailableException) {
            Popup.show(translations.pay.walleePaymentNotAvailable);
            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.outOfStockException) {
            let itemList = "<ul>";
            for (const i in error.additionalData.remaining)
                itemList +=
                    "<li>" +
                    Format.htmlEscape(error.additionalData.remaining[i].articleName) +
                    ": " +
                    error.additionalData.remaining[i].remaining +
                    "</li>";

            itemList += "</ul>";

            const msg = translations.pay.failedOutOfStock.replace("{0}", itemList);
            Popup.show(msg);
            this.checkState();
        } else if (error.type === exceptionTypes.offerExpiredException) {
            const reason = error.additionalData.reason;
            switch (reason) {
                case exceptionReasons.articlePriceChanged:
                case exceptionReasons.articleNotAvailable:
                    Popup.show(translations.pay.cartChangedAndPleaseWaitMessage, () => {
                        if (onClickBackBtn !== undefined) {
                            onClickBackBtn();
                        }
                    });
                    this.checkState();
                    break;
                default:
                    Popup.show(translations.pay.failedPaymentUnknownReason);
                    this.state = PaymentState.Failed;
                    this.onStateChanged.dispatch();
                    break;
            }
        } else if (error.type === exceptionTypes.emailNotConfirmedException) {
            Popup.show(translations.pay.failedEmailNotConfirmed);
            this.state = PaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.identityNotSignedUpException) {
            Popup.show(translations.pay.failedIdentityNotSignedUp);
            this.state = PaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.noFulfillmentSlotsAvailableException) {
            Popup.show(
                translations.pay.noFulfillmentSlotAvailable,
                () => {
                    if (this.onGoBackToFulfillmentTimeView !== undefined) {
                        this.onGoBackToFulfillmentTimeView();
                    }
                },
                translations.general.ok
            );

            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.newFulfillmentTimeAvailableException) {
            const newAvailableFulfillmentTime = new Date(error.additionalData.newAvailableFulfillmentTimeUtc);
            const timeMode = error.additionalData.timeMode as FulfillmentTimeMode;

            this.showNewFulfillmentSlotAvailablePopup(
                false,
                newAvailableFulfillmentTime,
                timeMode,
                onClickBackBtn,
                anonymousUserEmail,
                endUserEmail
            );
            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.newFulfillmentDateTimeAvailableException) {
            const newAvailableFulfillmentTime = new Date(error.additionalData.newAvailableFulfillmentTimeUtc);
            const timeMode = error.additionalData.timeMode as FulfillmentTimeMode;

            this.showNewFulfillmentSlotAvailablePopup(
                true,
                newAvailableFulfillmentTime,
                timeMode,
                onClickBackBtn,
                anonymousUserEmail,
                endUserEmail
            );

            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        } else {
            Popup.show(translations.pay.failedPaymentUnknownReason);
            this.state = PaymentState.Failed;
            this.onStateChanged.dispatch();
        }
    }

    private showNewFulfillmentSlotAvailablePopup(
        includeDate: boolean,
        newAvailableFulfillmentTime: Date,
        timeMode: FulfillmentTimeMode,
        onClickBackBtn?: () => void,
        anonymousUserEmail?: string,
        endUserEmail?: string
    ) {
        const message =
            timeMode === FulfillmentTimeMode.Time
                ? translations.pay.newFulfillmentSlotAvailable
                      .replace("{0}", translations.pay.scheduledTime)
                      .replace(
                          "{1}",
                          includeDate
                              ? DateTime.toDateTimeString(newAvailableFulfillmentTime)
                              : DateTime.toTimeString(newAvailableFulfillmentTime)
                      )
                : translations.pay.newFulfillmentSlotAvailable
                      .replace("{0}", translations.enums.fulfillmentTimeMode[FulfillmentTimeMode[timeMode]])
                      .replace(
                          "{1}",
                          translations.pay.approximately +
                              " " +
                              (includeDate
                                  ? DateTime.toDateTimeString(newAvailableFulfillmentTime)
                                  : DateTime.toTimeString(newAvailableFulfillmentTime))
                      );

        Popup.show(
            message,
            () => {
                this.pay([], this.consumption, true, onClickBackBtn, anonymousUserEmail, endUserEmail);
            },
            translations.general.accept,
            translations.general.cancel,
            undefined,
            undefined,
            true,
            true,
            () => {
                if (this.onGoBackToFulfillmentTimeView !== undefined) {
                    this.onGoBackToFulfillmentTimeView();
                }
            }
        );
    }
    private async onCheckoutBillWithWallee(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.walleeTransactionToAuthorize != null) {
            sendWalleeTransactionToAuthorize(paymentIntentConfirmHandle.walleeTransactionToAuthorize);
            return;
        }

        if (
            paymentIntentConfirmHandle.walleeOneClickUrl != null &&
            paymentIntentConfirmHandle.walleeOneClickUrl != ""
        ) {
            const windowObjectReference = window.open(paymentIntentConfirmHandle.walleeOneClickUrl, "_self");
            if (windowObjectReference == null) {
                window.location.href = paymentIntentConfirmHandle.walleeOneClickUrl;
            }
            return;
        }

        if (paymentIntentConfirmHandle.isPaymentDevice) {
            return;
        }

        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (!dataConfig.preventPaymentProvider) {
            try {
                await JavascriptLoader.load(paymentIntentConfirmHandle.walleeJavascriptUrl!, "wallee");
                this.walleeHandler = window["IframeCheckoutHandler"](
                    paymentIntentConfirmHandle.walleeSelectedPaymentId
                );
                ConnectionState.disabled = true;

                //check if selected payment method is twint, if so, alert user to go back to the app
                if (selectedPaymentMethod!.id === "twint") {
                    Popup.show(
                        translations.pay.twintReturnWarning,
                        () => {
                            this.resetCartIfPossible();
                            this.walleeHandler.submit();
                        },
                        translations.general.understood
                    );
                } else {
                    this.walleeHandler.submit();
                }
            } catch (ex) {
                logError(ex);
                Popup.show(translations.pay.failedPaymentUnknownReason + " " + (ex as any).toString());
            }
        }
    }

    private async onCheckoutWithWorldline(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.worldlineTransactionToAuthorize != null) {
            sendWorldlineTransactionToAuthorize(paymentIntentConfirmHandle.worldlineTransactionToAuthorize);
            return;
        } else {
            throw Error("No worldline transaction to authorize");
        }
    }

    private async onCheckoutCartWithWallee(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.walleeTransactionToAuthorize != null) {
            sendWalleeTransactionToAuthorize(paymentIntentConfirmHandle.walleeTransactionToAuthorize);
            return;
        }

        if (
            paymentIntentConfirmHandle.walleeOneClickUrl != null &&
            paymentIntentConfirmHandle.walleeOneClickUrl != ""
        ) {
            const windowObjectReference = window.open(paymentIntentConfirmHandle.walleeOneClickUrl, "_self");
            if (windowObjectReference == null) {
                window.location.href = paymentIntentConfirmHandle.walleeOneClickUrl;
            }
            return;
        }

        //No need to do any action
        if (paymentIntentConfirmHandle.isPaymentDevice) {
            return;
        }

        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (!dataConfig.preventPaymentProvider) {
            try {
                await JavascriptLoader.load(paymentIntentConfirmHandle.walleeJavascriptUrl!, "wallee");
                this.walleeHandler = window["IframeCheckoutHandler"](
                    paymentIntentConfirmHandle.walleeSelectedPaymentId
                );

                if (!dataConfig.preventPaymentProvider) {
                    ConnectionState.disabled = true;

                    //check if selected payment method is twint, if so, alert user to go back to the app
                    if (selectedPaymentMethod!.id === "twint") {
                        Popup.show(
                            translations.pay.twintReturnWarning,
                            () => {
                                this.resetCartIfPossible();
                                this.walleeHandler.submit();
                            },
                            translations.general.understood
                        );
                    } else {
                        this.resetCartIfPossible();
                        this.walleeHandler.submit();
                    }
                }
            } catch (ex) {
                logError(ex);
                // do nothing
            }
        }
    }

    public setPaymentMethod(method: IPaymentMethod, withCardToken?: ICreditCard): any {
        this.selectedPaymentMethod = method;
        this.selectedCreditCard = withCardToken;

        let returnHandler: any = undefined;

        if (method.provider === PaymentProvider.Wallee || method.provider === PaymentProvider.PostFinance) {
            this.stripeHandler = undefined;
            returnHandler = this.walleeHandler;
        } else if (method.provider === PaymentProvider.Stripe) {
            this.walleeHandler = undefined;
            returnHandler = (handler: { (): IStripeFormValues }) => {
                this.stripeHandler = handler;
            };
        } else {
            this.walleeHandler = undefined;
            this.stripeHandler = undefined;
        }

        if (
            !getIsApplyVouchersAllowedForOrderUseCase(method, Helper.getCurrentOrderServiceUseCase()) &&
            this.getCurrentDiscount() > 0
        ) {
            this.customerDao.removeAllCartDiscounts().catch((err) => {
                logError(err);
            });
        }

        this.checkState(true);
        return returnHandler;
    }

    public clearPaymentMethod() {
        this.selectedPaymentMethod = undefined;
        this.selectedCreditCard = undefined;
        this.walleeHandler = undefined;
        this.stripeHandler = undefined;
        this.checkState(true);
    }

    public renewConsumption() {
        this.consumption = this.getCurrentConsumption();
        this.discountAmount = this.getCurrentDiscount();
        this.totalExclTipAfterDiscounts = this.calculateCurrentTotalExclTipAfterDiscountsAndVouchers();
        this.updateTotal();
    }

    public reset() {
        this.state = PaymentState.Preparing;
        this.selectedPaymentMethod = undefined;
        this.selectedCreditCard = undefined;
        this.walleeHandler = undefined;
        this.stripeHandler = undefined;
        this.consumption = 0;
        this.total = 0;
        this.tipPercent = this.defaultTipPercent;
        this.tipAmount = 0;
        this.intent = undefined;
        this.paymentInProgress = false;
    }

    private updateTotal() {
        this.consumption = this.getCurrentConsumption();
        if (!!this.tipPercent || this.tipPercent === 0) {
            this.tipAmount = Misc.roundFiftyCents(this.tipPercent * this.consumption);
        }
        this.total = Misc.roundCents(this.consumption + this.tipAmount);
        this.onAmountChanged.dispatch();
        this.checkState();
    }

    private checkState(forceDispatch?: boolean) {
        let newState = PaymentState.Preparing;
        const isPayLater = this.isPayLater();
        if (this.paymentInProgress) {
            if (isPayLater) {
                newState = PaymentState.Ordering;
            } else {
                newState = PaymentState.Paying;
            }
        } else {
            if (this.intent === undefined || this.total < 0) {
                newState = PaymentState.Preparing;
            } else if (
                this.selectedPaymentMethod === undefined ||
                (this.selectedPaymentMethod.provider === PaymentProvider.Stripe &&
                    this.selectedCreditCard === undefined &&
                    this.stripeHandler === undefined)
            ) {
                newState = PaymentState.MethodSelection;
            } else {
                newState = PaymentState.ReadyToPay;
            }
        }

        if (newState !== this.state || forceDispatch !== undefined) {
            this.state = newState;
            this.onStateChanged.dispatch();
        }
    }

    private getCurrentConsumption(): number {
        if (TastierClient.context !== undefined) {
            if (Helper.isPayService()) {
                if (TastierClient.context.currentBill !== undefined) {
                    return TastierClient.context.getBillPayableAmount();
                }
            } else if (TastierClient.context.currentOffer?.cart !== undefined) {
                return TastierClient.context.currentOffer.cart.total;
            }
        }

        return 0;
    }

    private getCurrentDiscount(): number {
        if (TastierClient.context !== undefined) {
            if (Helper.isPayService()) {
                return TastierClient.context.currentBill?.discountAmount ?? 0;
            } else if (TastierClient.context.currentOffer?.cart !== undefined) {
                return TastierClient.context.currentOffer.cart.totalDiscountsAndVouchersAmount;
            }
        }

        return 0;
    }

    private calculateCurrentTotalExclTipAfterDiscountsAndVouchers(): number {
        if (TastierClient.context !== undefined) {
            if (Helper.isPayService()) {
                if (TastierClient.context.currentBill !== undefined) {
                    return (
                        TastierClient.context.getBillPayableAmount() + TastierClient.context.currentBill.discountAmount
                    );
                }
            } else if (TastierClient.context.currentOffer?.cart !== undefined) {
                return TastierClient.context.currentOffer.cart.totalAfterDiscountsAndVouchers;
            }
        }

        return 0;
    }

    public getCurrentUnpaidAmount(): number {
        if (TastierClient.context.currentBill == null) {
            return 0;
        }

        return Misc.roundCents(TastierClient.context.currentBill.unpaid);
    }
}
